import React from 'react';

/**
 * Classic numbered-page pagination.
 *
 * Props:
 *   page       current page (1-indexed)
 *   total      total record count
 *   pageSize   records per page
 *   onChange   (newPage) => void
 *
 * Renders nothing if total <= pageSize.
 *
 * Layout strategy for many pages:
 *   - Always show first, last, and current ± 2
 *   - Use … ellipsis to fill gaps
 *   - Mobile-friendly: numbers + "‹" / "›"
 */
export default function Pagination({ page, total, pageSize, onChange }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  if (totalPages <= 1) return null;

  const go = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    onChange(p);
  };

  // Build page number list with ellipses
  const pagesToShow = (() => {
    const result = new Set();
    result.add(1);
    result.add(totalPages);
    for (let i = page - 2; i <= page + 2; i++) {
      if (i >= 1 && i <= totalPages) result.add(i);
    }
    const sorted = [...result].sort((a, b) => a - b);
    // Insert ellipsis markers (we'll use 0 as a sentinel)
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push(0);
      out.push(sorted[i]);
    }
    return out;
  })();

  const firstIdx = (page - 1) * pageSize + 1;
  const lastIdx = Math.min(page * pageSize, total);

  const btnStyle = (active, disabled) => ({
    minWidth: 34,
    height: 34,
    padding: '0 10px',
    border: '1px solid ' + (active ? 'var(--blue, #3b82f6)' : 'var(--gray-200)'),
    background: active ? 'var(--blue, #3b82f6)' : 'white',
    color: active ? 'white' : (disabled ? 'var(--gray-300)' : 'var(--gray-700)'),
    borderRadius: 6,
    cursor: disabled ? 'default' : 'pointer',
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    transition: 'all 0.15s',
  });

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 12,
      marginTop: 16,
      padding: '12px 4px',
    }}>
      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
        {total > 0
          ? <>Εμφανίζονται <strong>{firstIdx}\u2013{lastIdx}</strong> από <strong>{total}</strong></>
          : 'Δεν υπάρχουν αποτελέσματα'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={() => go(page - 1)}
          disabled={page <= 1}
          style={btnStyle(false, page <= 1)}
          title="Προηγούμενη"
        >‹</button>
        {pagesToShow.map((p, i) => {
          if (p === 0) {
            return (
              <span key={'ellipsis-' + i} style={{ color: 'var(--gray-400)', padding: '0 4px' }}>…</span>
            );
          }
          return (
            <button
              key={p}
              onClick={() => go(p)}
              style={btnStyle(p === page, false)}
            >{p}</button>
          );
        })}
        <button
          onClick={() => go(page + 1)}
          disabled={page >= totalPages}
          style={btnStyle(false, page >= totalPages)}
          title="Επόμενη"
        >›</button>
      </div>
    </div>
  );
}
